import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { RankBadge, RankPlaque } from "@/lib/ranks";
import { PlatformIcons, PLATFORMS } from "@/lib/platforms";
import { US_STATES } from "@/lib/states";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const GAME_SHORT = { "CALL OF DUTY: WARZONE": "Warzone", "CALL OF DUTY: MW4 MULTIPLAYER": "MW4 MP" };
const TIER_LABEL = { qualifier: "Qualifier", semifinals: "Semifinals", championship: "Championship", test: "Test bracket" };

// Tracker Network profile URL builder — MOCKED derived stats until real API key is dropped in.
const trackerUrl = (game, tag) => {
    const enc = encodeURIComponent(tag);
    if (game === "CALL OF DUTY: MW4 MULTIPLAYER") return `https://tracker.gg/cod/profile/atvi/${enc}/overview`;
    return `https://tracker.gg/warzone/profile/atvi/${enc}/overview`;
};

// Deterministic pseudo-stats derived from ELO + record so the profile has numbers to show.
// MOCKED: replace with real Tracker Network API once TRACKER_API_KEY is provisioned.
const derivedStats = (elo, wins, losses) => {
    const games = wins + losses;
    const winPct = games ? (wins / games) : 0;
    const kd = +(0.55 + Math.max(0, (elo - 100)) / 2000).toFixed(2);
    const kpm = +(3 + Math.max(0, (elo - 100)) / 200).toFixed(1);
    const dmg = Math.round(500 + Math.max(0, (elo - 100)) * 0.55);
    return { kd, kpm, winPct: Math.round(winPct * 100), dmg };
};

export default function PlayerProfile() {
    const { tag } = useParams();
    const { user: authUser, refreshInvites, setUser } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        api.get(`/players/${encodeURIComponent(tag)}`)
            .then(r => setData(r.data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [tag, refreshTick]);

    if (loading) return <div className="pt-40 text-center text-white/40 font-mono text-xs tracking-[0.3em]">LOADING PROFILE…</div>;
    if (notFound || !data) return (
        <div className="pt-40 pb-40 text-center" data-testid="player-not-found">
            <div className="overline text-white/40 mb-4">[ Not Found ]</div>
            <h1 className="font-display font-black text-5xl md:text-7xl">/{tag}</h1>
            <p className="mt-6 text-white/50">No player with that gamer tag.</p>
            <Link to="/leaderboard" className="btn-ghost mt-8 inline-block">Back to rankings →</Link>
        </div>
    );

    const p = data.player;
    const teams = data.teams || [];
    const registrations = data.registrations || [];
    const wsowMatches = data.wsow_matches || [];
    const eloSeries = (p.elo_history || []).map(e => ({
        label: new Date(e.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        elo: e.elo,
    }));
    const minElo = Math.min(...eloSeries.map(e => e.elo), p.elo) - 40;
    const maxElo = Math.max(...eloSeries.map(e => e.elo), p.elo) + 40;

    const verified = p.id_verification_status === "verified";

    return (
        <div className="pt-[68px]" data-testid="player-profile-page">
            {/* HERO */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-20 pb-10">
                <div className="overline mb-4">[ Player Profile · Public ]</div>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
                    <div>
                        <h1 className="font-display font-black tracking-tighter leading-[0.86] text-5xl md:text-7xl lg:text-[7vw]" data-testid="player-tag">
                            /{p.gamer_tag}
                        </h1>
                        <div className="mt-5 flex flex-wrap items-center gap-3">
                            <span className="font-mono text-xs tracking-[0.24em] uppercase text-white/60">{p.name}</span>
                            {p.state && (
                                <>
                                    <span className="w-1 h-1 rounded-full bg-white/30" />
                                    <span className="font-mono text-xs tracking-[0.24em] uppercase text-white/60" data-testid="player-state">{p.state}</span>
                                </>
                            )}
                            {p.main_game && (
                                <>
                                    <span className="w-1 h-1 rounded-full bg-white/30" />
                                    <span className="font-mono text-xs tracking-[0.24em] uppercase text-volt">{GAME_SHORT[p.main_game] || p.main_game}</span>
                                </>
                            )}
                            <PlatformIcons user={p} size="sm" testId="profile-platforms" />
                            {verified && (
                                <span className="font-mono text-[10px] tracking-[0.24em] uppercase border border-volt/40 text-volt px-2 py-0.5">ID ✓</span>
                            )}
                            {p.role === "admin" && (
                                <span className="font-mono text-[10px] tracking-[0.24em] uppercase border border-signal/50 text-signal px-2 py-0.5">Admin</span>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-px bg-white/10 border border-white/10">
                        <StatCell label="ELO" value={p.elo} accent testId="player-stat-elo" />
                        <StatCell label="Wins" value={p.wins || 0} />
                        <StatCell label="Losses" value={p.losses || 0} />
                        <StatCell label="W/L" value={((p.wins || 0) / Math.max(1, ((p.wins || 0) + (p.losses || 0)))).toFixed(2)} />
                    </div>
                </div>
                <div className="mt-8 max-w-md">
                    <RankPlaque elo={p.elo} testId="player-rank-plaque" subtitle={p.rank?.next_min ? `Next tier at ${p.rank.next_min} ELO` : "Peak tier"} />
                </div>
            </section>

            {/* STATS · Tracker Network */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16" data-testid="stats-section">
                {(() => {
                    const s = derivedStats(p.elo, p.wins || 0, p.losses || 0);
                    const game = p.main_game || "CALL OF DUTY: WARZONE";
                    return (
                        <div className="border border-white/10 clip-frame bg-[#0A0A0A] p-6 md:p-8">
                            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                                <div>
                                    <div className="overline mb-2 flex items-center gap-2">
                                        <span>[ Stats · Tracker Network ]</span>
                                        <span className="font-mono text-[9px] tracking-[0.24em] uppercase border border-signal/40 text-signal px-1.5 py-0.5" title="Derived from ELO — live sync coming soon">MOCKED</span>
                                    </div>
                                    <h2 className="font-display font-black text-2xl md:text-3xl">The stat sheet.</h2>
                                </div>
                                <a href={trackerUrl(game, p.gamer_tag)} target="_blank" rel="noopener noreferrer"
                                   className="btn-primary !py-2.5 !px-4 !text-[10px]" data-testid="tracker-network-link">
                                    View live stats on Tracker →
                                </a>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
                                <StatCell label="K / D" value={s.kd} accent testId="stat-kd" />
                                <StatCell label="Kills / Match" value={s.kpm} testId="stat-kpm" />
                                <StatCell label="Win %" value={`${s.winPct}%`} testId="stat-winpct" />
                                <StatCell label="DMG / Match" value={s.dmg.toLocaleString()} testId="stat-dmg" />
                            </div>
                            <div className="mt-4 font-mono text-[10px] tracking-[0.22em] uppercase text-white/40">
                                Numbers derived from tournament ELO · {GAME_SHORT[game] || game}
                            </div>
                        </div>
                    );
                })()}
            </section>

            {/* CHANGE USERNAME / HOME STATE (own profile only) */}
            {authUser && authUser.id === p.id && (
                <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 grid md:grid-cols-2 gap-6" data-testid="change-username-section">
                    <UsernameEditor currentTag={p.gamer_tag} />
                    <StateEditor currentState={p.state} />
                </section>
            )}

            {/* LINKED PLATFORMS (edit — own profile only) */}
            {authUser && authUser.id === p.id && (
                <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16" data-testid="linked-platforms-section">
                    <PlatformLinker player={p} onSaved={() => setRefreshTick(t => t + 1)} />
                </section>
            )}

            {/* ELO TREND */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16" data-testid="elo-trend-section">
                <div className="border border-white/10 clip-frame bg-[#0A0A0A] p-6 md:p-8">
                    <div className="flex items-end justify-between mb-6">
                        <div>
                            <div className="overline mb-2">[ ELO Trend · 12 weeks ]</div>
                            <h2 className="font-display font-black text-2xl md:text-3xl">The climb.</h2>
                        </div>
                        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/40 text-right">
                            <div>Peak <span className="text-volt">{Math.max(...eloSeries.map(e => e.elo), p.elo)}</span></div>
                            <div>Trough <span className="text-white/60">{Math.min(...eloSeries.map(e => e.elo), p.elo)}</span></div>
                        </div>
                    </div>
                    <div className="h-[280px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={eloSeries} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                                <XAxis dataKey="label" stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 11, fontFamily: "monospace" }} />
                                <YAxis domain={[minElo, maxElo]} stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 11, fontFamily: "monospace" }} width={50} />
                                <Tooltip
                                    contentStyle={{ background: "#050505", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 0, fontFamily: "monospace", fontSize: 12 }}
                                    labelStyle={{ color: "#A855F7" }}
                                    itemStyle={{ color: "#fff" }}
                                />
                                <Line type="monotone" dataKey="elo" stroke="#A855F7" strokeWidth={2.5} dot={{ r: 3, fill: "#A855F7" }} activeDot={{ r: 6, fill: "#C084FC" }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </section>

            {/* TEAMS */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16" data-testid="teams-section">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="overline mb-2">[ Teams Captained ]</div>
                        <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl">Rosters.</h2>
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/40">{teams.length} team{teams.length === 1 ? "" : "s"}</div>
                </div>
                {teams.length === 0 ? (
                    <div className="border border-white/10 p-12 text-center text-white/40 font-display text-xl">No teams captained yet.</div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {teams.map(t => (
                            <div key={t.id} className="border border-white/10 clip-frame-sm p-5 bg-[#0A0A0A]" data-testid={`player-team-${t.tag}`}>
                                <div className="overline text-white/40 mb-2">{GAME_SHORT[t.game] || t.game}{t.test_team ? " · Test" : ""}</div>
                                <div className="font-display font-extrabold text-xl leading-tight">{t.name}</div>
                                <div className="font-mono text-[10px] tracking-[0.22em] text-volt mt-1">[{t.tag}]</div>
                                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                                    <span>{t.home_county || t.home_state || "—"}</span>
                                    <div className="flex items-center gap-2">
                                        <RankBadge elo={t.elo} size="xs" testId={`profile-team-rank-${t.tag}`} />
                                        <span className="text-volt font-display font-black text-base tracking-normal">{t.elo}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* WSOW MATCH LOG */}
            {wsowMatches.length > 0 && (
                <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16" data-testid="match-history-section">
                    <div className="flex items-end justify-between mb-6">
                        <div>
                            <div className="overline mb-2">[ Match History · WSOW ]</div>
                            <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl">Every drop.</h2>
                        </div>
                    </div>
                    <div className="border border-white/10 clip-frame overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-white/[0.03] border-b border-white/10">
                                <tr className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/45">
                                    <th className="text-left px-4 py-3">Tournament</th>
                                    <th className="text-left px-4 py-3">Team</th>
                                    <th className="text-right px-4 py-3">Match</th>
                                    <th className="text-right px-4 py-3">Placement</th>
                                    <th className="text-right px-4 py-3">Kills</th>
                                    <th className="text-right px-4 py-3">Points</th>
                                </tr>
                            </thead>
                            <tbody>
                                {wsowMatches.map(m => (
                                    <tr key={m.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`match-row-${m.id}`}>
                                        <td className="px-4 py-3">
                                            <div className="font-display font-extrabold text-sm">{m.tournament?.name || "—"}</div>
                                            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 mt-0.5">{GAME_SHORT[m.tournament?.game] || m.tournament?.game}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-display font-extrabold text-sm">{m.team?.name}</div>
                                            <div className="font-mono text-[10px] tracking-[0.2em] text-volt mt-0.5">[{m.team?.tag}]</div>
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono text-xs text-white/70">M{m.match_number}</td>
                                        <td className="px-4 py-3 text-right font-display font-black text-lg">#{m.placement}</td>
                                        <td className="px-4 py-3 text-right font-mono text-sm text-white/70">{m.kills}</td>
                                        <td className="px-4 py-3 text-right font-display font-black text-lg text-volt">{m.total_pts}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* BRACKET ENTRIES */}
            {registrations.length > 0 && (
                <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-32" data-testid="brackets-section">
                    <div className="flex items-end justify-between mb-6">
                        <div>
                            <div className="overline mb-2">[ Bracket Entries ]</div>
                            <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl">Circuits.</h2>
                        </div>
                    </div>
                    <div className="space-y-3">
                        {registrations.map((r, i) => (
                            <div key={i} className="border border-white/10 clip-frame-sm px-5 py-4 bg-[#0A0A0A] flex flex-wrap items-center justify-between gap-4" data-testid={`bracket-entry-${i}`}>
                                <div>
                                    <div className="overline text-white/40 mb-1">{TIER_LABEL[r.tournament?.bracket_tier] || "Bracket"} · {GAME_SHORT[r.tournament?.game] || "—"}</div>
                                    <Link to={r.tournament?.test_bracket ? `/bracket/${r.tournament.slug}` : `/tournaments/${r.tournament?.slug}`} className="font-display font-extrabold text-lg hover:text-volt transition-colors">
                                        {r.tournament?.name || "—"}
                                    </Link>
                                    <div className="font-mono text-[10px] tracking-[0.2em] text-volt mt-0.5">{r.team?.name} [{r.team?.tag}]</div>
                                </div>
                                <div className="flex gap-6 font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">
                                    <div><div className="text-white/40">Points</div><div className="text-volt font-display font-black text-lg normal-case tracking-normal mt-0.5">{r.points}</div></div>
                                    <div><div className="text-white/40">Matches</div><div className="text-white font-display font-black text-lg normal-case tracking-normal mt-0.5">{r.matches_played}</div></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}

const StatCell = ({ label, value, accent = false, testId }) => (
    <div className="bg-[#050505] px-6 py-5" data-testid={testId}>
        <div className="overline text-white/40 mb-1">{label}</div>
        <div className={`font-display font-black text-2xl md:text-3xl ${accent ? "text-volt" : "text-white"}`}>{value}</div>
    </div>
);

const PLACEHOLDERS = {
    Xbox: "e.g. VoidShot#1234",
    PSN: "e.g. voidshot_ps",
    Steam: "76561198000000000",
    Discord: "e.g. voidshot#4402 or voidshot",
};

const PlatformLinker = ({ player, onSaved }) => {
    const [form, setForm] = useState({
        xbox_gamertag: player.xbox_gamertag || "",
        psn_id: player.psn_id || "",
        steam_id: player.steam_id || "",
        discord_tag: player.discord_tag || "",
    });
    const [saving, setSaving] = useState(false);
    const dirty =
        (form.xbox_gamertag || "") !== (player.xbox_gamertag || "") ||
        (form.psn_id || "") !== (player.psn_id || "") ||
        (form.steam_id || "") !== (player.steam_id || "") ||
        (form.discord_tag || "") !== (player.discord_tag || "");

    const save = async () => {
        setSaving(true);
        try {
            await api.post("/auth/platforms", form);
            toast.success("Platforms updated");
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    return (
        <div className="border border-white/10 clip-frame bg-[#0A0A0A] p-6 md:p-8">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                    <div className="overline mb-2">[ Linked Platforms · You ]</div>
                    <h2 className="font-display font-black text-2xl md:text-3xl">Every lobby you're in.</h2>
                    <p className="mt-2 text-white/50 text-sm max-w-xl">Attach Xbox, PSN, Steam, and Discord so squads and admins can find you across every platform — captains can DM you on Discord instead of waiting in-site.</p>
                </div>
                <button onClick={save} disabled={!dirty || saving}
                    className="btn-primary !py-2.5 !px-4 !text-[10px] disabled:opacity-40"
                    data-testid="platforms-save-btn">
                    {saving ? "Saving…" : dirty ? "Save changes →" : "Saved ✓"}
                </button>
            </div>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                {PLATFORMS.map(p => (
                    <label key={p.key} className="flex items-center gap-3 border border-white/10 bg-[#050505] px-3 py-2 focus-within:border-volt/60 transition-colors" data-testid={`platform-field-${p.label.toLowerCase()}`}>
                        <span aria-hidden className="w-9 h-9 flex-shrink-0 flex items-center justify-center font-display font-black text-lg" style={{ background: p.color, color: "#fff" }}>{p.short}</span>
                        <div className="flex-1 min-w-0">
                            <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-white/40">{p.label}</div>
                            <input
                                type="text"
                                value={form[p.key]}
                                onChange={(e) => setForm({ ...form, [p.key]: e.target.value })}
                                placeholder={PLACEHOLDERS[p.label] || ""}
                                data-testid={`platform-input-${p.label.toLowerCase()}`}
                                maxLength={64}
                                className="w-full bg-transparent outline-none font-body text-sm text-white placeholder-white/25 pt-0.5"
                            />
                        </div>
                    </label>
                ))}
            </div>
        </div>
    );
};


const USERNAME_RE = /^[A-Za-z0-9_.\-]{2,24}$/;

const UsernameEditor = ({ currentTag }) => {
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const [value, setValue] = useState(currentTag || "");
    const [saving, setSaving] = useState(false);
    const trimmed = value.trim();
    const dirty = trimmed && trimmed !== currentTag;
    const valid = USERNAME_RE.test(trimmed);

    const save = async () => {
        if (!dirty || !valid) return;
        setSaving(true);
        try {
            const r = await api.post("/auth/gamer-tag", { gamer_tag: trimmed });
            const newTag = r.data?.gamer_tag || trimmed;
            // Refresh /auth/me and hop to new profile URL
            const me = await api.get("/auth/me");
            setUser(me.data);
            toast.success(`Username updated to /${newTag}`);
            navigate(`/player/${encodeURIComponent(newTag)}`, { replace: true });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Rename failed");
        } finally { setSaving(false); }
    };

    return (
        <div className="border border-white/10 clip-frame bg-[#0A0A0A] p-6 md:p-8" data-testid="change-username-card">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
                <div>
                    <div className="overline mb-2">[ Username · You ]</div>
                    <h2 className="font-display font-black text-2xl md:text-3xl">Change your handle.</h2>
                    <p className="mt-2 text-white/50 text-sm max-w-xl">
                        Your public tag on rosters, leaderboards, and match reports. 2–24 characters — letters, numbers, dots, dashes or underscores.
                    </p>
                </div>
                <button
                    onClick={save}
                    disabled={!dirty || !valid || saving}
                    className="btn-primary !py-2.5 !px-4 !text-[10px] disabled:opacity-40"
                    data-testid="username-save-btn"
                >
                    {saving ? "Saving…" : dirty ? "Update username →" : "Saved ✓"}
                </button>
            </div>
            <label className="block max-w-lg">
                <span className="overline text-white/50">Gamer tag</span>
                <div className="mt-2 flex items-stretch border border-white/15 focus-within:border-volt/60 transition-colors bg-void">
                    <span className="px-3 flex items-center font-display font-black text-volt text-lg select-none">/</span>
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="voidshot"
                        maxLength={24}
                        data-testid="username-input"
                        className="flex-1 bg-transparent outline-none px-2 py-3 font-mono text-base text-white"
                    />
                </div>
                <div className="mt-2 font-mono text-[10px] tracking-[0.22em] uppercase text-white/40 flex items-center justify-between">
                    <span>Current: <span className="text-volt">/{currentTag}</span></span>
                    <span>
                        {trimmed.length}/24
                        {trimmed && !valid && (
                            <span className="ml-3 text-signal" data-testid="username-invalid-hint">Invalid format</span>
                        )}
                    </span>
                </div>
            </label>
        </div>
    );
};

const StateEditor = ({ currentState }) => {
    const { setUser } = useAuth();
    const [value, setValue] = useState(currentState || "");
    const [saving, setSaving] = useState(false);
    const dirty = value && value !== (currentState || "");
    const currentLabel = US_STATES.find(s => s[0] === (currentState || "").toUpperCase())?.[1] || "—";

    const save = async () => {
        if (!dirty) return;
        setSaving(true);
        try {
            await api.post("/auth/state", { state: value });
            const me = await api.get("/auth/me");
            setUser(me.data);
            toast.success(`Home state set to ${value}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    return (
        <div className="border border-white/10 clip-frame bg-[#0A0A0A] p-6 md:p-8" data-testid="state-editor-card">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
                <div>
                    <div className="overline mb-2">[ Home state · You ]</div>
                    <h2 className="font-display font-black text-2xl md:text-3xl">Where you play from.</h2>
                    <p className="mt-2 text-white/50 text-sm max-w-xl">
                        Determines which state brackets you're eligible for. Shows next to your name on your profile and roster cards.
                    </p>
                </div>
                <button
                    onClick={save}
                    disabled={!dirty || saving}
                    className="btn-primary !py-2.5 !px-4 !text-[10px] disabled:opacity-40"
                    data-testid="state-save-btn"
                >
                    {saving ? "Saving…" : dirty ? "Update state →" : "Saved ✓"}
                </button>
            </div>
            <label className="block max-w-md">
                <span className="overline text-white/50">State</span>
                <select
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    data-testid="state-select"
                    className="mt-2 w-full bg-void border border-white/15 focus:border-volt/60 outline-none px-3 py-3 font-mono text-sm text-white transition-colors"
                >
                    <option value="">— Select state —</option>
                    {US_STATES.map(([code, name]) => (
                        <option key={code} value={code}>{code} · {name}</option>
                    ))}
                </select>
                <div className="mt-2 font-mono text-[10px] tracking-[0.22em] uppercase text-white/40">
                    Current: <span className="text-volt">{currentState || "—"} · {currentLabel}</span>
                </div>
            </label>
        </div>
    );
};

